import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Send } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

interface CategoryProposalModalProps {
  trigger?: React.ReactNode;
}

const CategoryProposalModal = ({ trigger }: CategoryProposalModalProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [suggestedIcon, setSuggestedIcon] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim() || !description.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Erreur",
          description: "Vous devez être connecté pour proposer une catégorie",
          variant: "destructive",
        });
        return;
      }

      const { error } = await (supabase.from as any)('category_proposals')
        .insert({
          user_id: user.id,
          name: name.trim(),
          description: description.trim(),
          suggested_icon: suggestedIcon.trim() || null,
          status: 'pending'
        });

      if (error) throw error;

      toast({
        title: "Succès !",
        description: "Votre proposition de catégorie a été soumise pour examen",
      });

      // Reset form
      setName("");
      setDescription("");
      setSuggestedIcon("");
      setOpen(false);
    } catch (error) {
      console.error('Error submitting category proposal:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la soumission",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="gap-2">
      <Plus className="h-4 w-4" />
      Proposer une catégorie
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Proposer une nouvelle catégorie</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nom de la catégorie *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Mobilité urbaine"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description/Justification *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Expliquez pourquoi cette catégorie serait utile..."
              rows={3}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="icon">Icône suggérée (optionnel)</Label>
            <Input
              id="icon"
              value={suggestedIcon}
              onChange={(e) => setSuggestedIcon(e.target.value)}
              placeholder="Ex: Bus, Bicycle, etc."
            />
            <p className="text-xs text-muted-foreground">
              Nom d'icône Lucide React ou description
            </p>
          </div>
          
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 gap-2"
            >
              <Send className="h-4 w-4" />
              {isSubmitting ? "Envoi..." : "Soumettre"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryProposalModal;